import { useState, useEffect, useCallback } from "react";
import { parseCookies, setCookie } from "nookies";

import { Cookies } from "lib/cookies";

import { IS_SERVER } from "lib/config";
import { NextPageContext } from "next";
import { THEME_KEY } from "constants/localStorageKeys";

export enum ThemeMode {
  Light = "light",
  Dark = "dark",
}

const DEFAULT_THEME_MODE: ThemeMode = ThemeMode.Dark;
const COOKIE_OPTIONS: any = {
  path: "/",
  maxAge: 30 * 24 * 60 * 60, // 30 days
  sameSite: "lax",
};

export const getUIThemeCookie = (ctx?: NextPageContext) => {
  return parseCookies(ctx)[Cookies.UITheme];
};

const setUIThemeCookie = (theme: ThemeMode) => {
  setCookie(null, Cookies.UITheme, theme, COOKIE_OPTIONS);
};

const getSystemThemePreference = (): ThemeMode => {
  if (IS_SERVER || typeof window.matchMedia !== "function") {
    return DEFAULT_THEME_MODE;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? ThemeMode.Dark
    : ThemeMode.Light;
};

export const useTheme = () => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    try {
      const savedModeLocalStorage = localStorage?.getItem(THEME_KEY) as ThemeMode | null;
      if (savedModeLocalStorage) {
        return savedModeLocalStorage as ThemeMode;
      }

      const savedModeCookie = getUIThemeCookie();
      if (savedModeCookie) {
        return savedModeCookie as ThemeMode;
      }

      return getSystemThemePreference();
    } catch {
      return DEFAULT_THEME_MODE;
    }
  });

  useEffect(() => {
    localStorage?.setItem(THEME_KEY, mode);
    setUIThemeCookie(mode);
  }, [mode]);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === ThemeMode.Light ? ThemeMode.Dark : ThemeMode.Light));
  }, []);

  return { mode, toggleMode };
};
